import { useEffect, useState, useCallback } from "react";
import { io } from "socket.io-client";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { IoNotifications, IoCheckmarkCircle } from "react-icons/io5";
import { Card, Slide } from "@mui/material";
import getSocketUrl from "config/socket";
import { getAccessToken, isSecondaryUser } from "utils/auth";

const playNotificationSound = () => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  oscillator.frequency.value = 800;
  oscillator.type = "sine";
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.5);
};

function NotificationSystem({ onNewDeposit }) {
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);

  const pushNotification = useCallback(
    (data) => {
      try {
        playNotificationSound();
      } catch {
        // Silent error
      }

      const notification = {
        id: data.id || Date.now(),
        ...data,
        timestamp: new Date(),
      };

      setNotifications((prev) => [notification, ...prev].slice(0, 5));

      if (onNewDeposit) {
        onNewDeposit(data);
      }

      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      }, 10000);
    },
    [onNewDeposit]
  );

  useEffect(() => {
    if (isSecondaryUser()) {
      return undefined;
    }

    const token = getAccessToken();
    if (!token) {
      return undefined;
    }

    const socket = io(getSocketUrl(), {
      auth: { token },
      withCredentials: true,
      transports: ["websocket", "polling"],
      path: "/socket.io",
    });

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));
    socket.on("nuevo-deposito", pushNotification);

    return () => {
      socket.off("nuevo-deposito", pushNotification);
      socket.close();
    };
  }, [pushNotification]);

  if (isSecondaryUser()) {
    return null;
  }

  const handleDismiss = (notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  };

  return (
    <VuiBox
      sx={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 9999,
        maxWidth: "400px",
      }}
    >
      <VuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          p: 1,
          borderRadius: "8px",
          background: connected
            ? "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)"
            : "linear-gradient(127.09deg, rgba(40, 6, 6, 0.94) 19.41%, rgba(35, 10, 10, 0.49) 76.65%)",
        }}
      >
        <IoNotifications size={16} color={connected ? "#0075FF" : "#999"} />
        <VuiTypography variant="caption" color={connected ? "text" : "secondary"}>
          {connected ? "Notificaciones activas" : "Conectando..."}
        </VuiTypography>
      </VuiBox>

      {notifications.map((notification) => (
        <Slide key={notification.id} direction="left" in mountOnEnter unmountOnExit>
          <Card
            sx={{
              mb: 2,
              p: 2,
              background:
                "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)",
              backdropFilter: "blur(42px)",
              border: "2px solid #0075FF",
              boxShadow: "0px 0px 20px rgba(0, 117, 255, 0.5)",
              cursor: "pointer",
            }}
            onClick={() => handleDismiss(notification.id)}
          >
            <VuiBox display="flex" alignItems="flex-start" gap={2}>
              <IoCheckmarkCircle size={24} color="#0DDA88" />
              <VuiBox flex={1}>
                <VuiTypography variant="button" color="white" fontWeight="bold">
                  ¡Nuevo depósito recibido!
                </VuiTypography>
                <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                  <strong>Monto:</strong> S/ {parseFloat(notification.monto || 0).toFixed(2)}
                </VuiTypography>
                <VuiTypography variant="caption" color="text" display="block">
                  <strong>De:</strong> {notification.nombre || "Sin nombre"}
                </VuiTypography>
                <VuiTypography variant="caption" color="text" display="block">
                  <strong>Banco:</strong> {notification.banco || "Desconocido"}
                </VuiTypography>
                <VuiTypography variant="caption" color="secondary" display="block" mt={1}>
                  Hace {Math.floor((Date.now() - notification.timestamp) / 1000)}s
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </Card>
        </Slide>
      ))}
    </VuiBox>
  );
}

export default NotificationSystem;
